// Admin · Bunny Storage manager
// A dedicated /admin/bunny-storage workspace that lets the team:
//   • Browse folders + thumbnails (full-screen grid, breadcrumb)
//   • Multi-select with checkboxes
//   • Bulk delete (with confirmation) + per-item delete
//   • Upload files via drag-and-drop or file picker (with per-file
//     progress bars; uploads queue in parallel)
//   • Create new folders
//   • Copy the public CDN URL of any file to the clipboard
//   • Sort by name / size / last-changed; search inside the current folder
//
// The Bunny AccessKey lives ONLY in the FastAPI process — the browser
// talks to /api/admin/bunny-storage/* proxy endpoints, gated by
// X-Admin-Password.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, Folder, FolderPlus, Loader2, Upload, Trash2,
  Search, AlertCircle, Database, X, Copy, Check, RefreshCw, ImageIcon,
  ArrowUpAZ, CalendarClock, FileDigit, ExternalLink,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { devLog } from "../lib/devLog";

const API = process.env.REACT_APP_BACKEND_URL || "";
const SORTS = [
  { id: "name", label: "Name (A–Z)", icon: ArrowUpAZ },
  { id: "date", label: "Newest first", icon: CalendarClock },
  { id: "size", label: "Size (largest)", icon: FileDigit },
];

const formatBytes = (b) => {
  if (!b || b < 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const AdminBunnyStorageInner = () => {
  const headers = useMemo(() => adminHeaders(), []);
  const [prefix, setPrefix] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [imagesOnly, setImagesOnly] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [selected, setSelected] = useState(() => new Set()); // set of paths
  const [busy, setBusy] = useState(""); // "delete" | "mkdir" | ""
  const [confirmDelete, setConfirmDelete] = useState(null); // { paths: [...], label: "..." }
  const [mkdirOpen, setMkdirOpen] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [preview, setPreview] = useState(null);    // selected file object for the side preview
  const [uploads, setUploads] = useState([]);      // [{ id, name, total, loaded, status, error }]
  const [dragOver, setDragOver] = useState(false);
  const [copiedPath, setCopiedPath] = useState("");
  const fileInputRef = useRef(null);
  const dropCounter = useRef(0);

  // ── Listing ───────────────────────────────────────────────────────────
  const fetchList = useCallback(async (nextPrefix, opts = {}) => {
    if (!opts.silent) setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({
        prefix: nextPrefix || "",
        images_only: String(imagesOnly),
      });
      const res = await fetch(`${API}/api/admin/bunny-storage/list?${qs.toString()}`, {
        headers,
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data.objects) ? data.objects : []);
    } catch (err) {
      setError(err.message || "Failed to load Bunny listing");
      setItems([]);
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [headers, imagesOnly]);

  useEffect(() => { fetchList(prefix); }, [fetchList, prefix]);

  // Reset selection + preview whenever the folder or sort/filter changes.
  useEffect(() => { setSelected(new Set()); setPreview(null); }, [prefix, imagesOnly]);

  // ── Derived ───────────────────────────────────────────────────────────
  const segments = useMemo(() => {
    const parts = (prefix || "").replace(/\/$/, "").split("/").filter(Boolean);
    const acc = [];
    let cum = "";
    parts.forEach((p) => {
      cum = cum ? `${cum}/${p}` : p;
      acc.push({ label: p, path: `${cum}/` });
    });
    return acc;
  }, [prefix]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? items.filter((it) => it.name.toLowerCase().includes(q))
      : items;
    const sorted = [...base];
    sorted.sort((a, b) => {
      // Folders always above files.
      if (a.is_directory !== b.is_directory) return a.is_directory ? -1 : 1;
      if (sortBy === "size") return (b.size || 0) - (a.size || 0);
      if (sortBy === "date") {
        const da = new Date(a.last_changed || 0).getTime();
        const db = new Date(b.last_changed || 0).getTime();
        return db - da;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
    return sorted;
  }, [items, query, sortBy]);

  const fileCount = filtered.filter((it) => !it.is_directory).length;
  const folderCount = filtered.length - fileCount;

  // ── Selection helpers ─────────────────────────────────────────────────
  const toggleSelect = (path) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };
  const selectAllVisible = () => {
    const all = new Set(selected);
    filtered.forEach((it) => all.add(it.path));
    setSelected(all);
  };
  const clearSelection = () => setSelected(new Set());

  // ── Upload ────────────────────────────────────────────────────────────
  const uploadOne = useCallback((file) => new Promise((resolve) => {
    const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploads((u) => [...u, { id, name: file.name, total: file.size, loaded: 0, status: "uploading", error: "" }]);
    const xhr = new XMLHttpRequest();
    const fd = new FormData();
    fd.append("prefix", prefix || "");
    fd.append("file", file);
    xhr.open("POST", `${API}/api/admin/bunny-storage/upload`);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      setUploads((u) => u.map((up) => (up.id === id ? { ...up, loaded: e.loaded } : up)));
    };
    xhr.onload = () => {
      const ok = xhr.status >= 200 && xhr.status < 300;
      const detail = (() => {
        try { return JSON.parse(xhr.responseText).detail || ""; } catch (err) { devLog.debug("[AdminBunny] parse response failed", err); return ""; }
      })();
      setUploads((u) => u.map((up) => {
        if (up.id !== id) return up;
        return {
          ...up,
          status: ok ? "done" : "error",
          loaded: ok ? up.total : up.loaded,
          error: ok ? "" : (detail || `HTTP ${xhr.status}`),
        };
      }));
      resolve(ok);
    };
    xhr.onerror = () => {
      setUploads((u) => u.map((up) => (up.id === id ? { ...up, status: "error", error: "Network error" } : up)));
      resolve(false);
    };
    xhr.send(fd);
  }), [prefix, headers]);

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    await Promise.all(list.map((f) => uploadOne(f)));
    fetchList(prefix, { silent: true });
  }, [uploadOne, fetchList, prefix]);

  // ── Drag & drop (whole-page surface) ──────────────────────────────────
  useEffect(() => {
    const onDragEnter = (e) => {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
      dropCounter.current += 1;
      setDragOver(true);
    };
    const onDragLeave = () => {
      dropCounter.current -= 1;
      if (dropCounter.current <= 0) {
        dropCounter.current = 0;
        setDragOver(false);
      }
    };
    const onDragOver = (e) => { e.preventDefault(); };
    const onDrop = (e) => {
      e.preventDefault();
      dropCounter.current = 0;
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (files && files.length) handleFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [handleFiles]);

  // ── Delete ────────────────────────────────────────────────────────────
  const requestDelete = (paths, label) => {
    const arr = (paths || []).filter(Boolean);
    if (!arr.length) return;
    setConfirmDelete({ paths: arr, label: label || `${arr.length} item${arr.length === 1 ? "" : "s"}` });
  };
  const performDelete = async () => {
    if (!confirmDelete) return;
    setBusy("delete");
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/bunny-storage/delete-bulk`, {
      credentials: "include",
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ paths: confirmDelete.paths }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Delete failed: ${txt.slice(0, 200)}`);
      }
      setConfirmDelete(null);
      clearSelection();
      setPreview(null);
      fetchList(prefix);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  // ── Create folder ─────────────────────────────────────────────────────
  const performMkdir = async () => {
    const name = newFolder.trim();
    if (!name) return;
    setBusy("mkdir");
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/bunny-storage/folder`, {
      credentials: "include",
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ prefix: prefix || "", name }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Create folder failed: ${txt.slice(0, 200)}`);
      }
      setMkdirOpen(false);
      setNewFolder("");
      fetchList(prefix);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  // ── Copy URL ──────────────────────────────────────────────────────────
  const copyUrl = async (url, path) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath((p) => (p === path ? "" : p)), 1800);
    } catch (err) {
      // Clipboard API may be unavailable (http contexts, older browsers,
      // sandboxed frames). Log for diagnosis without breaking the UI.
      console.warn("[AdminBunnyStorage] clipboard.writeText failed:", err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  const selectedFiles = filtered.filter((it) => selected.has(it.path));
  const selectedBytes = selectedFiles.reduce((acc, it) => acc + (it.size || 0), 0);

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-bunny-storage-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-[#2C2621]/10 px-4 md:px-8 py-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]" data-testid="admin-bunny-back">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> Admin
          </Link>
          <span className="text-[#2C2621]/30">/</span>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-[#C16542]" strokeWidth={1.8} />
            <h1 className="font-serif-x text-2xl">Bunny Storage</h1>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => fetchList(prefix)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors disabled:opacity-40"
            data-testid="admin-bunny-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />}
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setMkdirOpen(true)}
            className="inline-flex items-center gap-1.5 border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-bunny-mkdir"
          >
            <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.6} /> New folder
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-bunny-upload"
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={1.8} /> Upload files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            data-testid="admin-bunny-upload-input"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {/* Breadcrumb + filters */}
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
          <button
            type="button"
            onClick={() => { setPrefix(""); setQuery(""); }}
            disabled={!prefix}
            className="px-2 py-1 hover:text-[#C16542] disabled:opacity-40"
            data-testid="admin-bunny-crumb-root"
          >
            Root
          </button>
          {segments.map((seg, i) => (
            <React.Fragment key={seg.path}>
              <ChevronRight className="w-3 h-3 text-[#5C5248]/60" />
              <button
                type="button"
                onClick={() => { setPrefix(seg.path); setQuery(""); }}
                className={`px-2 py-1 hover:text-[#C16542] truncate max-w-[260px] ${i === segments.length - 1 ? "text-[#2C2621] font-medium" : ""}`}
                title={seg.label}
                data-testid={`admin-bunny-crumb-${i}`}
              >
                {seg.label}
              </button>
            </React.Fragment>
          ))}
          <span className="flex-1" />
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.6} />
            <input
              type="search"
              placeholder="Filter the current folder…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542] bg-white w-64"
              data-testid="admin-bunny-search"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2 py-1.5 text-xs border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542] bg-white"
            data-testid="admin-bunny-sort"
          >
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs text-[#5C5248] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={imagesOnly}
              onChange={(e) => setImagesOnly(e.target.checked)}
              data-testid="admin-bunny-images-only"
            />
            Images only
          </label>
        </div>

        {/* Selection bar */}
        {selected.size > 0 ? (
          <div className="mt-3 flex items-center gap-3 bg-[#FBF6EE] border border-[#C16542]/20 px-3 py-2" data-testid="admin-bunny-selection-bar">
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248]">{selected.size} selected · {formatBytes(selectedBytes)}</span>
            <button type="button" onClick={selectAllVisible} className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] underline-offset-2 hover:underline">Select all visible</button>
            <button type="button" onClick={clearSelection} className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] underline-offset-2 hover:underline">Clear</button>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => requestDelete(Array.from(selected), `${selected.size} selected item${selected.size === 1 ? "" : "s"}`)}
              className="inline-flex items-center gap-1.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
              data-testid="admin-bunny-bulk-delete"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete selected
            </button>
          </div>
        ) : null}
      </header>

      {/* Drag overlay */}
      {dragOver ? (
        <div className="fixed inset-0 z-[200] bg-[#1A1513]/80 backdrop-blur-sm flex items-center justify-center pointer-events-none" data-testid="admin-bunny-drag-overlay">
          <div className="bg-[#FDFBF7] px-12 py-10 text-center">
            <Upload className="w-12 h-12 mx-auto text-[#C16542]" strokeWidth={1.4} />
            <p className="mt-4 font-serif-x text-2xl">Drop to upload to</p>
            <p className="mt-1 text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">/{prefix.replace(/\/$/, "") || "(root)"}</p>
          </div>
        </div>
      ) : null}

      {/* Main panel: grid + preview side */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-0 px-4 md:px-8 py-6">
        {/* Grid */}
        <div data-testid="admin-bunny-grid-section">
          {error ? (
            <p className="text-sm text-[#C16542] mb-4" data-testid="admin-bunny-error">
              <AlertCircle className="w-3.5 h-3.5 inline-block align-[-2px] mr-1" />
              {error}
            </p>
          ) : null}
          {loading ? (
            <p className="text-sm text-[#5C5248]" data-testid="admin-bunny-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline-block align-[-2px] mr-1" />
              Loading…
            </p>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-[#2C2621]/15 px-6 py-10 text-center" data-testid="admin-bunny-empty">
              <p className="font-serif-x text-xl mb-2">This folder is empty.</p>
              <p className="text-sm text-[#5C5248]">Drag files anywhere on the page or hit “Upload files” to add some.</p>
            </div>
          ) : (
            <>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-3" data-testid="admin-bunny-counts">
                {folderCount} folder{folderCount === 1 ? "" : "s"} · {fileCount} file{fileCount === 1 ? "" : "s"}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3" data-testid="admin-bunny-grid">
                {filtered.map((it) => {
                  const isSel = selected.has(it.path);
                  const key = `${it.path}-${it.guid || it.name}`;
                  if (it.is_directory) {
                    return (
                      <div key={key} className="relative group" data-testid={`admin-bunny-folder-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}>
                        <button
                          type="button"
                          onClick={() => { setPrefix(it.path.endsWith("/") ? it.path : `${it.path}/`); setQuery(""); }}
                          className="w-full aspect-[4/3] bg-[#F2EBE1] hover:bg-[#E8DBC4] border border-[#2C2621]/10 flex items-center justify-center transition-colors"
                          title={`Open ${it.name}`}
                        >
                          <Folder className="w-10 h-10 text-[#5C5248]" strokeWidth={1.4} />
                        </button>
                        <label className="absolute top-1.5 left-1.5 inline-flex items-center justify-center w-5 h-5 bg-white/85 border border-[#2C2621]/20 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSelect(it.path.endsWith("/") ? it.path : `${it.path}/`)}
                            className="w-3 h-3"
                            aria-label={`Select ${it.name}`}
                            data-testid={`admin-bunny-select-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => requestDelete([it.path.endsWith("/") ? it.path : `${it.path}/`], it.name)}
                          className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-7 h-7 bg-white/90 hover:bg-[#C16542] hover:text-[#FDFBF7] border border-[#2C2621]/15 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete folder"
                          aria-label={`Delete folder ${it.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                        </button>
                        <p className="mt-1 text-[11px] text-[#2C2621] truncate" title={it.name}>{it.name}</p>
                      </div>
                    );
                  }
                  return (
                    <div key={key} className="relative group" data-testid={`admin-bunny-file-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}>
                      <button
                        type="button"
                        onClick={() => setPreview(it)}
                        className={`w-full aspect-[4/3] bg-[#F2EBE1] border overflow-hidden flex items-center justify-center transition-colors ${isSel ? "border-[#C16542] ring-2 ring-[#C16542]/40" : "border-[#2C2621]/10 hover:border-[#C16542]"}`}
                        title={`Preview ${it.name}`}
                      >
                        {it.public_url ? (
                          <img
                            src={`${it.public_url}?width=320&format=webp&quality=70`}
                            alt={it.name}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { e.currentTarget.src = it.public_url; }}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-[#5C5248]" strokeWidth={1.4} />
                        )}
                      </button>
                      <label className="absolute top-1.5 left-1.5 inline-flex items-center justify-center w-5 h-5 bg-white/85 border border-[#2C2621]/20 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelect(it.path)}
                          className="w-3 h-3"
                          aria-label={`Select ${it.name}`}
                          data-testid={`admin-bunny-select-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                        />
                      </label>
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {it.public_url ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); copyUrl(it.public_url, it.path); }}
                            className="inline-flex items-center justify-center w-7 h-7 bg-white/90 hover:bg-[#2C2621] hover:text-[#FDFBF7] border border-[#2C2621]/15"
                            title="Copy CDN URL"
                            aria-label={`Copy CDN URL for ${it.name}`}
                            data-testid={`admin-bunny-copy-${it.name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                          >
                            {copiedPath === it.path ? <Check className="w-3.5 h-3.5" strokeWidth={2.2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.6} />}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); requestDelete([it.path], it.name); }}
                          className="inline-flex items-center justify-center w-7 h-7 bg-white/90 hover:bg-[#C16542] hover:text-[#FDFBF7] border border-[#2C2621]/15"
                          title="Delete file"
                          aria-label={`Delete ${it.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                        </button>
                      </div>
                      <p className="mt-1 text-[11px] text-[#2C2621] truncate" title={it.name}>{it.name}</p>
                      <p className="text-[10px] text-[#5C5248] truncate">{formatBytes(it.size)}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Side preview panel — sticky */}
        <aside className="hidden lg:block ml-6">
          <div className="sticky top-[148px] border border-[#2C2621]/10 bg-white" data-testid="admin-bunny-preview-panel">
            {preview ? (
              <>
                <div className="aspect-[4/3] bg-[#F2EBE1] overflow-hidden">
                  {preview.public_url ? (
                    <img src={preview.public_url} alt={preview.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-[#5C5248]" strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 space-y-2">
                  <p className="text-sm font-medium text-[#2C2621] break-all">{preview.name}</p>
                  <div className="text-[11px] text-[#5C5248] grid grid-cols-[80px_1fr] gap-y-1">
                    <span>Folder</span><span className="text-[#2C2621] break-all">/{preview.parent.replace(/\/$/, "") || "(root)"}</span>
                    <span>Size</span><span className="text-[#2C2621]">{formatBytes(preview.size)}</span>
                    <span>Changed</span><span className="text-[#2C2621]">{formatDate(preview.last_changed)}</span>
                    <span>Type</span><span className="text-[#2C2621]">{preview.content_type || "—"}</span>
                  </div>
                  {preview.public_url ? (
                    <div className="space-y-1.5">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">CDN URL</p>
                      <div className="flex items-center gap-1">
                        <code className="flex-1 text-[10px] text-[#2C2621] bg-[#FBF6EE] px-2 py-1.5 break-all">{preview.public_url}</code>
                        <button
                          type="button"
                          onClick={() => copyUrl(preview.public_url, preview.path)}
                          className="inline-flex items-center justify-center w-8 h-8 border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542]"
                          title="Copy URL"
                          data-testid="admin-bunny-preview-copy"
                        >
                          {copiedPath === preview.path ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <a
                          href={preview.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542]"
                          title="Open in new tab"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => requestDelete([preview.path], preview.name)}
                    className="w-full inline-flex items-center justify-center gap-1.5 border border-[#C16542] text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                    data-testid="admin-bunny-preview-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete this file
                  </button>
                </div>
              </>
            ) : (
              <div className="px-4 py-10 text-center">
                <ImageIcon className="w-10 h-10 mx-auto text-[#5C5248]/50" strokeWidth={1.2} />
                <p className="mt-3 text-sm text-[#5C5248]">Click any file to preview it here.</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Upload tray (bottom-right) */}
      {uploads.length > 0 ? (
        <div className="fixed bottom-4 right-4 w-80 max-w-[calc(100vw-2rem)] bg-white border border-[#2C2621]/15 shadow-2xl z-40" data-testid="admin-bunny-upload-tray">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#2C2621]/10">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
              {uploads.filter((u) => u.status === "uploading").length > 0
                ? `Uploading ${uploads.filter((u) => u.status === "uploading").length} of ${uploads.length}`
                : `Uploads · ${uploads.filter((u) => u.status === "done").length}/${uploads.length} done`}
            </p>
            <button
              type="button"
              onClick={() => setUploads([])}
              className="text-[#5C5248] hover:text-[#C16542]"
              aria-label="Dismiss"
              data-testid="admin-bunny-upload-tray-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto divide-y divide-[#2C2621]/10">
            {uploads.map((u) => {
              const pct = u.total > 0 ? Math.min(100, Math.round((u.loaded / u.total) * 100)) : 0;
              return (
                <li key={u.id} className="px-3 py-2 text-[11px] text-[#2C2621]" data-testid={`admin-bunny-upload-item-${u.status}`}>
                  {(() => {
                    const STATUS_LABEL = { done: "Done", error: "Failed" };
                    const STATUS_BAR = { done: "bg-[#1F7A4D]", error: "bg-[#C16542]" };
                    const label = STATUS_LABEL[u.status] || `${pct}%`;
                    const barCls = STATUS_BAR[u.status] || "bg-[#C16542]";
                    const barWidth = u.status === "done" ? 100 : pct;
                    return (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="truncate flex-1" title={u.name}>{u.name}</span>
                          <span className={u.status === "error" ? "text-[#C16542]" : "text-[#5C5248]"}>{label}</span>
                        </div>
                        <div className="mt-1 h-1 bg-[#F2EBE1]">
                          <div
                            className={`h-full ${barCls}`}
                            style={{ width: `${barWidth}%`, transition: "width 0.2s ease" }}
                          />
                        </div>
                        {u.error ? <p className="mt-1 text-[10px] text-[#C16542]">{u.error}</p> : null}
                      </>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Create folder modal */}
      {mkdirOpen ? (
        <div className="fixed inset-0 z-50 bg-[#1A1513]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setMkdirOpen(false)} data-testid="admin-bunny-mkdir-modal">
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#2C2621]/10 flex items-center justify-between">
              <h2 className="font-serif-x text-xl">New folder</h2>
              <button type="button" onClick={() => setMkdirOpen(false)} className="text-[#5C5248] hover:text-[#C16542]"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-2">Folder name</p>
              <input
                type="text"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") performMkdir(); }}
                placeholder="e.g. 7. New Hotel"
                className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
                autoFocus
                data-testid="admin-bunny-mkdir-input"
              />
              <p className="mt-2 text-[11px] text-[#5C5248]">Will be created inside <code className="text-[#2C2621]">/{prefix.replace(/\/$/, "") || "(root)"}</code>.</p>
            </div>
            <div className="px-5 py-3 border-t border-[#2C2621]/10 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setMkdirOpen(false)} className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] px-3 py-2">Cancel</button>
              <button
                type="button"
                onClick={performMkdir}
                disabled={!newFolder.trim() || busy === "mkdir"}
                className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-4 py-2 text-[10px] tracking-[0.25em] uppercase disabled:opacity-40"
                data-testid="admin-bunny-mkdir-confirm"
              >
                {busy === "mkdir" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
                Create folder
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete confirmation modal */}
      {confirmDelete ? (
        <div className="fixed inset-0 z-50 bg-[#1A1513]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => busy !== "delete" && setConfirmDelete(null)} data-testid="admin-bunny-delete-modal">
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#2C2621]/10 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#C16542]" />
              <h2 className="font-serif-x text-xl">Delete {confirmDelete.label}?</h2>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-[#2C2621]">
                This will permanently delete the selected item{confirmDelete.paths.length === 1 ? "" : "s"} from Bunny Storage.
                Folders are deleted recursively (every file inside is gone too).
              </p>
              <p className="mt-2 text-[11px] text-[#C16542]">
                Pages referencing these URLs on the live site will start showing broken images. Make sure they’re not in use.
              </p>
              {confirmDelete.paths.length <= 5 ? (
                <ul className="mt-3 max-h-32 overflow-y-auto text-[11px] text-[#5C5248] space-y-0.5">
                  {confirmDelete.paths.map((p) => <li key={p} className="break-all">• /{p}</li>)}
                </ul>
              ) : null}
            </div>
            <div className="px-5 py-3 border-t border-[#2C2621]/10 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={busy === "delete"}
                className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] px-3 py-2 disabled:opacity-40"
                data-testid="admin-bunny-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performDelete}
                disabled={busy === "delete"}
                className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-4 py-2 text-[10px] tracking-[0.25em] uppercase disabled:opacity-40"
                data-testid="admin-bunny-delete-confirm"
              >
                {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />}
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const AdminBunnyStoragePage = () => (
  <AdminAuthGate>
    <AdminBunnyStorageInner />
  </AdminAuthGate>
);

export default AdminBunnyStoragePage;
